import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import { contextOriginService } from '@app/auth/contextOriginService'

/**
 * Embedding and sign-in telemetry for Datadog RUM and browser logs.
 *
 * Records whether lender web runs inside another site's frame, which sites embed it,
 * and how each sign-in step ends, so the hosted sign-in plan can be scoped from data.
 * Only host names, booleans and error codes are recorded: no emails, full URLs,
 * query strings or tokens. Nothing here may throw into the app.
 *
 * The RUM app keeps only error events, so the counts come from the logs: one log per page
 * load plus one per sign-in step, each carrying the embedding context.
 */

export const EMBEDDING_CONTEXT_KEY = 'embedding'
export const SIGNIN_STEP_ACTION = 'signin_step'
export const EMBEDDING_CONTEXT_LOG = 'embedding_context'

// Its own logger at info: the shared level (DD_LOGS_LEVEL) is error, which would drop these logs.
export const embeddingLogger = datadogLogs.createLogger('embeddingLogger', {
  level: 'info',
  // eslint-disable-next-line no-undef
  handler: DD_LOGS_HANDLER || 'console',
  context: {
    domain: 'embedding',
  },
})

export const SIGNIN_STEPS = {
  SESSION_CHECK: 'session_check',
  METHOD_SELECTED: 'method_selected',
  SSO_REDIRECT_STARTED: 'sso_redirect_started',
  PASSWORD: 'password',
  REDIRECT_CALLBACK: 'redirect_callback',
}

const hostOf = (value) => {
  if (!value || typeof value !== 'string') {
    return null
  }
  try {
    return new URL(value).hostname || null
  } catch (error) {
    return null
  }
}

const readRgOrigin = () => {
  try {
    return contextOriginService.getToken()
  } catch (error) {
    return null
  }
}

const isFramed = (win) => {
  try {
    return win.self !== win.top
  } catch (error) {
    // Reading window.top can throw in some sandboxed frames; that only happens when framed.
    return true
  }
}

/**
 * Facts about where this page is running. Pure apart from reading the given window/document.
 * @param {{ win?: Window, doc?: Document }} [env]
 * @returns {{ embedded: boolean, ancestor_hosts: string[] | null, top_host: string | null,
 *   referrer_host: string | null, rg_origin: string | null }}
 */
export function getEmbeddingContext({ win = window, doc = document } = {}) {
  const embedded = isFramed(win)
  // location.ancestorOrigins is in Chrome and Safari, not Firefox. Nearest ancestor first.
  const ancestors = win.location && win.location.ancestorOrigins
  const ancestorHosts = ancestors ? Array.from(ancestors).map(hostOf).filter(Boolean) : null

  return {
    embedded,
    ancestor_hosts: ancestorHosts,
    top_host: ancestorHosts && ancestorHosts.length > 0 ? ancestorHosts.at(-1) : null,
    referrer_host: hostOf(doc.referrer),
    rg_origin: readRgOrigin(),
  }
}

/**
 * Whether this document can use its unpartitioned cookies right now (Storage Access API).
 * @param {Document} [doc]
 * @returns {Promise<boolean | null>} null when the browser has no Storage Access API
 */
export async function getStorageAccess(doc = document) {
  if (!doc || typeof doc.hasStorageAccess !== 'function') {
    return null
  }
  try {
    return await doc.hasStorageAccess()
  } catch (error) {
    return null
  }
}

// The last context set, so sign-in steps can refresh rg_origin without re-reading the page.
let currentContext = null

const setEmbeddingContext = (context, { rum, logger }) => {
  currentContext = context
  try {
    logger.setContextProperty(EMBEDDING_CONTEXT_KEY, context)
  } catch (error) {
    // Telemetry must never break the app.
  }
  try {
    rum.setGlobalContextProperty(EMBEDDING_CONTEXT_KEY, context)
  } catch (error) {
    // Telemetry must never break the app.
  }
}

// rg_origin is read at page load, but a redirect sign-in stores it later on the same page.
const refreshRgOrigin = (deps) => {
  if (!currentContext) {
    return
  }
  const rgOrigin = readRgOrigin()
  if (rgOrigin !== currentContext.rg_origin) {
    setEmbeddingContext({ ...currentContext, rg_origin: rgOrigin }, deps)
  }
}

/**
 * Attach the embedding facts to every RUM event and every embedding log, then log the page load.
 * Call once, after the Datadog SDKs are initialised.
 * @param {{ rum?: object, logger?: object, win?: Window, doc?: Document }} [deps]
 * @returns {Promise<void>}
 */
export async function initEmbeddingTelemetry({
  rum = datadogRum,
  logger = embeddingLogger,
  win = window,
  doc = document,
} = {}) {
  try {
    const context = getEmbeddingContext({ win, doc })
    setEmbeddingContext(context, { rum, logger })
    const storageAccess = await getStorageAccess(doc)
    // currentContext, not context: a sign-in step may have refreshed rg_origin during the await.
    setEmbeddingContext({ ...currentContext, storage_access: storageAccess }, { rum, logger })
    logger.info(EMBEDDING_CONTEXT_LOG)
  } catch (error) {
    // Telemetry must never break the app.
  }
}

/**
 * Record one sign-in step as a log and a RUM action.
 * @param {string} step - one of SIGNIN_STEPS
 * @param {{ method?: string, outcome?: string, error_code?: string | number, screen?: string }} [details]
 * @param {{ rum?: object, logger?: object }} [deps]
 */
export function trackSignInStep(step, details = {}, { rum = datadogRum, logger = embeddingLogger } = {}) {
  refreshRgOrigin({ rum, logger })
  try {
    logger.info(SIGNIN_STEP_ACTION, { step, ...details })
  } catch (error) {
    // Telemetry must never break the app.
  }
  try {
    rum.addAction(SIGNIN_STEP_ACTION, { step, ...details })
  } catch (error) {
    // Telemetry must never break the app.
  }
}

/**
 * A short, non-identifying code for a sign-in error.
 * @param {*} error
 * @returns {string | number | null}
 */
export function signInErrorCode(error) {
  if (!error) {
    return null
  }
  return error.code || error.status || (error.response && error.response.status) || 'unknown'
}
